// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';

import { Dash } from '@/components/greenlight/Dash';
import { formatCountCompact, formatCountFull } from '@/components/greenlight/formatCount';
import { AD_CARD, AD_EYEBROW } from '@/components/greenlight/surfaces';
import { useIsMobile } from '@/lib/useIsMobile';
import { cn } from '@/lib/utils';
import type { AppSummary, AppUsageSummaryResult } from '@greenlight/shared';

function Stat({
  label,
  value,
  testId,
  className,
}: {
  label: string;
  value: number | undefined;
  testId: string;
  className?: string;
}) {
  // Three tiles share a phone's width, so six-digit counts read as "184.3K" there.
  const format = useIsMobile() ? formatCountCompact : formatCountFull;
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col justify-between gap-1.5 bg-card px-4 py-4 md:px-5',
        className,
      )}
      data-testid={testId}
    >
      <span className={AD_EYEBROW}>{label}</span>
      <span className="text-[24px] font-extrabold leading-none tracking-tight tabular-nums text-foreground md:text-[30px]">
        {value === undefined ? <Dash /> : format(value)}
      </span>
    </div>
  );
}

/**
 * Org-admin fleet banner. `usage` is a separate, slower read, so its two
 * figures show a dash until it lands rather than holding the whole banner back.
 */
export function AppsAdminBanner({
  summary,
  usage,
}: {
  summary: AppSummary;
  usage: AppUsageSummaryResult | undefined;
}): React.JSX.Element {
  // The live fleet, matching the filter chips' All: deleted apps are terminal.
  const apps = summary.total - summary.by_status.deleted;
  return (
    <section
      aria-label="Fleet at a glance"
      className={cn(
        AD_CARD,
        'grid gap-px overflow-hidden bg-border/70 md:grid-cols-[minmax(0,1fr)_minmax(0,3fr)]',
      )}
      data-testid="apps-admin-banner"
    >
      {/* A standing total, not a 7-day figure, so it sits apart from the windowed three. */}
      <Stat
        label="Total apps"
        value={apps}
        testId="apps-admin-banner-apps"
        className="justify-center bg-secondary"
      />
      <div className="flex flex-col gap-px" data-testid="apps-admin-banner-window">
        <p className={cn(AD_EYEBROW, 'bg-card px-4 py-2.5 md:px-5')}>Last 7 days</p>
        <div className="grid flex-1 grid-cols-3 gap-px">
          <Stat
            label="Updates shipped"
            value={summary.deploys_last_7_days}
            testId="apps-admin-banner-deploys"
          />
          <Stat
            label="Active users"
            value={usage?.active_users_total}
            testId="apps-admin-banner-people"
          />
          <Stat
            label="Data requests"
            value={usage?.data_calls_total}
            testId="apps-admin-banner-data-calls"
          />
        </div>
      </div>
    </section>
  );
}
